import { useIsFetching } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import { useLocation } from "react-router-dom";
import { ROUTE_PROGRESS_ENABLED } from "../../constants";
import { shouldTrackRouteNavigation } from "./routeProgressPolicy";

type ProgressState = "idle" | "running" | "completing";

const MINIMUM_VISIBLE_MS = 240;
const COMPLETION_ANIMATION_MS = 180;
const ROUTE_SETTLE_MS = 120;

const clearTimer = (timerRef: MutableRefObject<number | null>) => {
  if (timerRef.current !== null) {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

const RouteProgressBar = () => {
  const location = useLocation();
  const [progressState, setProgressState] = useState<ProgressState>("idle");
  const progressStateRef = useRef<ProgressState>("idle");
  const startedAtRef = useRef(0);
  const previousLocationKeyRef = useRef(location.key);
  const fetchCountRef = useRef(0);
  const completionTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const settleTimerRef = useRef<number | null>(null);

  const activeInitialFetches = useIsFetching({
    predicate: (query) => query.state.data === undefined,
  });
  fetchCountRef.current = activeInitialFetches;

  const startProgress = useCallback(() => {
    if (!ROUTE_PROGRESS_ENABLED) return;

    clearTimer(completionTimerRef);
    clearTimer(hideTimerRef);

    if (progressStateRef.current !== "running") {
      startedAtRef.current = performance.now();
    }

    progressStateRef.current = "running";
    setProgressState("running");
  }, []);

  const completeProgress = useCallback(() => {
    if (!ROUTE_PROGRESS_ENABLED || progressStateRef.current === "idle") return;

    clearTimer(completionTimerRef);
    const elapsed = performance.now() - startedAtRef.current;
    const remainingVisibleTime = Math.max(0, MINIMUM_VISIBLE_MS - elapsed);

    completionTimerRef.current = window.setTimeout(() => {
      progressStateRef.current = "completing";
      setProgressState("completing");

      clearTimer(hideTimerRef);
      hideTimerRef.current = window.setTimeout(() => {
        progressStateRef.current = "idle";
        setProgressState("idle");
      }, COMPLETION_ANIMATION_MS);
    }, remainingVisibleTime);
  }, []);

  useEffect(() => {
    if (!ROUTE_PROGRESS_ENABLED) return;

    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented || !(event.target instanceof Element)) return;

      const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;

      const shouldTrack = shouldTrackRouteNavigation({
        currentHref: window.location.href,
        destinationHref: anchor.href,
        target: anchor.target,
        downloadsFile: anchor.hasAttribute("download"),
        mouseButton: event.button,
        hasModifierKey: event.metaKey || event.ctrlKey || event.shiftKey || event.altKey,
      });

      if (shouldTrack) startProgress();
    };

    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("popstate", startProgress);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("popstate", startProgress);
    };
  }, [startProgress]);

  useEffect(() => {
    if (!ROUTE_PROGRESS_ENABLED) return;

    if (previousLocationKeyRef.current === location.key) return;
    previousLocationKeyRef.current = location.key;
    startProgress();

    clearTimer(settleTimerRef);
    settleTimerRef.current = window.setTimeout(() => {
      if (fetchCountRef.current === 0) completeProgress();
    }, ROUTE_SETTLE_MS);
  }, [completeProgress, location.key, startProgress]);

  useEffect(() => {
    if (!ROUTE_PROGRESS_ENABLED) return;

    if (activeInitialFetches > 0) {
      startProgress();
      return;
    }

    completeProgress();
  }, [activeInitialFetches, completeProgress, startProgress]);

  useEffect(() => () => {
    clearTimer(completionTimerRef);
    clearTimer(hideTimerRef);
    clearTimer(settleTimerRef);
  }, []);

  if (!ROUTE_PROGRESS_ENABLED || progressState === "idle") return null;

  return (
    <div
      className={`ui-route-progress ${progressState === "completing" ? "ui-route-progress-completing" : ""}`}
      role="progressbar"
      aria-label="Loading page"
    >
      <span className="ui-route-progress-bar" />
    </div>
  );
};

export default RouteProgressBar;
